<template>
  <div>
    <PageTitle title="Accounting Report" />
    <v-container fluid>
      <v-card class="pa-4 mb-4">
        <v-row align="end">
          <v-col cols="12" sm="6" md="2">
            <v-text-field v-model="start" label="From" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field v-model="end" label="To" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="accountId"
              :items="accountOptions"
              item-text="label"
              item-value="id"
              label="Account"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="accountType"
              :items="accountTypes"
              label="Account type"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="paymentType"
              :items="paymentTypes"
              label="Payment type"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="sourceType"
              :items="sources"
              label="Source"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="query" label="Search" outlined dense hide-details clearable />
          </v-col>
          <v-col cols="12" sm="auto" class="d-flex">
            <v-btn color="primary" height="40" :loading="loading" @click="load">Apply</v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="mb-4">
        <v-card-title class="text-subtitle-1">Accounts</v-card-title>
        <v-data-table
          :headers="accountHeaders"
          :items="accounts"
          :loading="loading"
          item-key="id"
          dense
          :items-per-page="10"
        >
          <template v-slot:item.available_balance="{ item }">{{ item.available_balance | formatCurrency }}</template>
          <template v-slot:item.period_in="{ item }">{{ item.period_in | formatCurrency }}</template>
          <template v-slot:item.period_out="{ item }">{{ item.period_out | formatCurrency }}</template>
          <template v-slot:item.by_payment_type="{ item }">
            <span v-if="!typeText(item)">—</span>
            <span v-else>{{ typeText(item) }}</span>
          </template>
        </v-data-table>
      </v-card>

      <v-card>
        <v-card-title class="text-subtitle-1">Transactions</v-card-title>
        <v-data-table
          :headers="transactionHeaders"
          :items="transactions"
          :loading="loading"
          item-key="id"
          dense
          :items-per-page="15"
        >
          <template v-slot:item.amount="{ item }">{{ item.amount | formatCurrency }}</template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import axios from "@/plugins/axios";

export default {
  data() {
    return {
      start: moment().startOf("month").format("YYYY-MM-DD"),
      end: moment().format("YYYY-MM-DD"),
      accountId: null,
      accountType: null,
      paymentType: null,
      sourceType: null,
      query: "",
      loading: false,
      accounts: [],
      transactions: [],
      accountOptions: [],
      accountTypes: [],
      paymentTypes: ["Cash", "Card", "Cheque", "Income", "Expense"],
      sources: ["order", "reservation", "sale", "purchase", "income", "expense"],
      accountHeaders: [
        { text: "Account", value: "nick_name" },
        { text: "Number", value: "account_number" },
        { text: "Type", value: "account_type" },
        { text: "Balance", value: "available_balance", align: "right" },
        { text: "In", value: "period_in", align: "right" },
        { text: "Out", value: "period_out", align: "right" },
        { text: "By payment type", value: "by_payment_type" },
      ],
      transactionHeaders: [
        { text: "Date", value: "date" },
        { text: "Reference", value: "reference_number" },
        { text: "Account", value: "account_name" },
        { text: "Source", value: "source" },
        { text: "Payment type", value: "payment_type" },
        { text: "Direction", value: "direction" },
        { text: "Status", value: "status" },
        { text: "Amount", value: "amount", align: "right" },
      ],
    };
  },
  methods: {
    typeText(item) {
      const types = item.by_payment_type || {};
      return Object.keys(types)
        .map((key) => `${key} ${types[key]}`)
        .join(", ");
    },
    load() {
      this.loading = true;
      axios
        .get("accounting/accounts-report", {
          params: {
            start: this.start,
            end: this.end,
            accountId: this.accountId || undefined,
            accountType: this.accountType || undefined,
            paymentType: this.paymentType || undefined,
            sourceType: this.sourceType || undefined,
            query: this.query || undefined,
          },
        })
        .then((res) => {
          const data = res.data.data || {};
          this.accounts = data.accounts || [];
          this.transactions = data.transactions || [];
          this.accountTypes = data.account_types || [];
          this.paymentTypes = data.payment_types || this.paymentTypes;
          this.sources = data.sources || this.sources;
          this.accountOptions = (data.account_options || []).map((account) => ({
            ...account,
            label: `${account.nick_name} (${account.account_number})`,
          }));
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.$toast.error("Could not load the accounting report");
        });
    },
  },
  created() {
    this.load();
  },
};
</script>
